import { targetPanelId } from './placement';
import type { Panel, WidgetPanel } from './types/panel';
import type { PanelItem } from './types/panelItem';
import type { PanelViewState, PlacedItem } from './types/panelModel';
import type { PanelId, PanelPlacement } from './types/panelPlacement';

// --- Panel model (fold + view-state) ---

export const DEFAULT_VIEW_STATE: PanelViewState = {
  activePanelId: null,
  isCollapsed: true,
  titles: {},
};

const byMessageId = (a: PanelItem, b: PanelItem): number => a.messageId.localeCompare(b.messageId);

/** Tab label for a placement; `target` routes into an existing panel, so its label is a fallback. */
const titleOf = (placement: PanelPlacement): string => (placement.strategy === 'target' ? 'Panel' : placement.title);

const minMessageId = (panel: WidgetPanel): string =>
  panel.items.reduce((min, item) => (min === '' || item.messageId < min ? item.messageId : min), '');

export const placeItems = (placed: PlacedItem[]): WidgetPanel[] => {
  const byId = new Map<PanelId, WidgetPanel>();
  const order: PanelId[] = [];
  const replaceIds = new Set<PanelId>();

  for (const { item, placement } of placed) {
    const id = targetPanelId(placement, item);
    if (placement.strategy === 'replace') {
      replaceIds.add(id);
    }
    const existing = byId.get(id);
    if (existing) {
      existing.items.push(item);
      continue;
    }
    byId.set(id, {
      id,
      role: placement.strategy === 'shared' || placement.strategy === 'replace' ? placement.role : undefined,
      title: titleOf(placement),
      kind: 'auto',
      source: 'widgets',
      items: [item],
    });
    order.push(id);
  }

  // Order tabs by first appearance (using every item), then keep only the latest for `replace` panels.
  return order
    .sort((a, b) => minMessageId(byId.get(a) as WidgetPanel).localeCompare(minMessageId(byId.get(b) as WidgetPanel)))
    .map((id) => {
      const panel = byId.get(id) as WidgetPanel;
      const sorted = [...panel.items].sort(byMessageId);
      return { ...panel, items: replaceIds.has(id) ? sorted.slice(-1) : sorted };
    });
};

export const applyPanelTitles = (panels: Panel[], titles: Record<PanelId, string> = {}): Panel[] =>
  panels.map((panel) => {
    const custom = titles[panel.id];
    return custom ? { ...panel, title: custom } : panel;
  });

export const resolveActivePanelId = (panels: Panel[], preferred: PanelId | null): PanelId | null => {
  if (preferred !== null && panels.some((panel) => panel.id === preferred)) {
    return preferred;
  }
  return panels[0]?.id ?? null;
};
